import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import { usePageMeta } from "@/hooks/usePageMeta";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cashmyproperty.ai/" },
    { "@type": "ListItem", position: 2, name: "Services", item: "https://cashmyproperty.ai/services" },
    { "@type": "ListItem", position: 3, name: "Joint Development (JDA)", item: "https://cashmyproperty.ai/services/jda" },
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is a Joint Development Agreement (JDA)?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A JDA is a legal partnership where you contribute your land and a developer funds and executes the construction. You receive 35–45% of the developed built-up area or its equivalent value — without investing any capital. Your name stays on the title deed throughout the agreement.",
      },
    },
    {
      "@type": "Question",
      name: "How long does a JDA take from start to income?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Construction typically takes 12–24 months depending on project scale. Our matching and legal structuring phase takes 6–10 weeks. Once construction is complete, you either receive possession of your share of units or begin receiving revenue share from sales/rentals.",
      },
    },
    {
      "@type": "Question",
      name: "What if the developer abandons the project midway?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We include milestone-based bank guarantees, performance clauses, and strong exit provisions in every JDA we structure. If a developer defaults, you retain ownership of your land plus whatever has been constructed. Our legal team ensures you are fully protected.",
      },
    },
    {
      "@type": "Question",
      name: "What minimum land size is needed for a JDA?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "For apartment or villa projects, minimum 1,000 sq.m (approximately 10,800 sq.ft) is required. Larger plots (5,000+ sq.m) unlock premium developer interest and better terms. For commercial JDA, 2,000+ sq.m is preferred in urban and semi-urban areas.",
      },
    },
  ],
};

const steps = [
  {
    step: "01",
    title: "AI Feasibility Report",
    desc: "We analyze your land's HBU (Highest & Best Use) score — zoning, FSI, demand, and comparable projects within a 5 km radius. Delivered in 48 hours.",
    icon: "ri-cpu-line",
  },
  {
    step: "02",
    title: "Developer Shortlisting",
    desc: "Our AI matches your land with our vetted network of 200+ developers. We present a ranked shortlist of 3–5 developers with proven track records in your area.",
    icon: "ri-user-search-line",
  },
  {
    step: "03",
    title: "Site Visit & Proposal",
    desc: "We coordinate site visits with shortlisted developers. Each submits a formal proposal with share percentage, timeline, and project plan. You choose the best fit.",
    icon: "ri-map-pin-line",
  },
  {
    step: "04",
    title: "Legal Structuring",
    desc: "Our legal team drafts the JDA — covering revenue share, construction milestones, bank guarantees, dispute resolution, and exit clauses. Fully vetted and owner-protective.",
    icon: "ri-file-text-line",
  },
  {
    step: "05",
    title: "Construction & Handover",
    desc: "Developer begins construction. We monitor progress with monthly reports. Upon completion, you receive possession of your share of units or revenue share — whichever you prefer.",
    icon: "ri-building-4-line",
  },
];

const benefits = [
  { icon: "ri-shield-check-line", title: "Zero Investment", desc: "You contribute only land. The developer funds 100% of construction, approvals, and marketing costs." },
  { icon: "ri-percent-line", title: "35–45% Share", desc: "Receive 35–45% of the total built-up area or its cash equivalent. On a 50-unit project, that is 17–22 units — yours." },
  { icon: "ri-home-2-line", title: "Retain Ownership", desc: "Your name stays on the title deed. This is a development partnership, not a sale. Your family's asset remains intact." },
  { icon: "ri-line-chart-line", title: "Value Appreciation", desc: "Developed land is worth 3–5x more than raw land. A ₹1 Cr plot can become a ₹3–4 Cr asset post-development." },
  { icon: "ri-team-line", title: "Vetted Developers", desc: "Only developers with 10+ completed projects, clean legal history, and strong financials enter our network." },
  { icon: "ri-scales-3-line", title: "Legal Protection", desc: "Bank guarantees, milestone-linked payments, penalty clauses, and strong exit provisions protect you at every stage." },
];

const faqs = [
  { q: "What is a Joint Development Agreement (JDA)?", a: "A JDA is a legal partnership where you contribute your land and a developer funds and executes the construction. You receive 35–45% of the developed built-up area or its equivalent value — without investing any capital. Your name stays on the title deed throughout the agreement." },
  { q: "How long does a JDA take from start to income?", a: "Construction typically takes 12–24 months depending on project scale. Our matching and legal structuring phase takes 6–10 weeks. Once construction is complete, you either receive possession of your share of units or begin receiving revenue share from sales/rentals." },
  { q: "What if the developer abandons the project midway?", a: "We include milestone-based bank guarantees, performance clauses, and strong exit provisions in every JDA we structure. If a developer defaults, you retain ownership of your land plus whatever has been constructed. Our legal team ensures you are fully protected." },
  { q: "What minimum land size is needed for a JDA?", a: "For apartment or villa projects, minimum 1,000 sq.m (approximately 10,800 sq.ft) is required. Larger plots (5,000+ sq.m) unlock premium developer interest and better terms. For commercial JDA, 2,000+ sq.m is preferred in urban and semi-urban areas." },
];

export default function JDAServicePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  usePageMeta({
    title: "Joint Development Agreement (JDA) — Monetize Land Without Selling | CashMyProperty.ai",
    description: "Convert your idle land into developed real estate without investing a single rupee. Our AI matches you with vetted developers for Joint Development Agreements. You contribute land, the developer builds — you get 35–45% share. Full legal protection, zero investment, retain ownership.",
    canonicalUrl: "https://cashmyproperty.ai/services/jda",
  });

  return (
    <PageLayout>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      {/* Hero */}
      <section className="relative w-full min-h-[480px] md:min-h-[520px] overflow-hidden bg-background-50">
        <img
          src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20luxury%20residential%20high-rise%20apartment%20complex%20under%20construction%20in%20Indian%20city%2C%20cranes%20and%20scaffolding%20against%20dramatic%20golden%20hour%20sky%2C%20warm%20amber%20sunlight%20hitting%20building%20facades%2C%20cinematic%20real%20estate%20development%20photography%2C%20deep%20teal%20sky%20with%20golden%20highlights%2C%20ultra%20detailed%20architecture%2C%20India%20urban%20landscape&width=1800&height=800&seq=service-detail-jda-hero&orientation=landscape"
          alt="Joint Development Agreement — CashMyProperty.ai"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-background-50/40" />
        <div className="relative h-full flex items-center container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 mb-5">
              <span className="text-white text-xs font-medium uppercase tracking-widest">Service Detail</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-[1.1] mb-4">
              <span className="text-white [text-shadow:0_0_24px_rgba(0,0,0,0.92)]">Joint Development</span>
              <br />
              <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-transparent drop-shadow-[0_0_16px_rgba(0,0,0,0.78)]">Agreement (JDA)</span>
            </h1>
            <p className="text-lg text-white/90 leading-relaxed max-w-lg [text-shadow:0_0_16px_rgba(0,0,0,0.84)]">
              Your land. Their capital. Shared success. Get 35–45% of developed property — without investing a single rupee.
            </p>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="py-4 bg-background-50 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-4-line mr-1" />Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to="/services" className="hover:text-primary-500 transition-colors whitespace-nowrap">Services</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-950 font-medium">Joint Development (JDA)</span>
          </nav>
        </div>
      </section>

      {/* What is JDA */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">What is JDA?</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
                Land + Capital = Shared Success
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-4">
                A Joint Development Agreement is the most powerful tool for landowners with high-value urban or semi-urban plots. Instead of selling your land for a one-time lump sum, you partner with a professional developer who brings construction expertise and capital.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                The developer designs, funds, builds, and markets the project — apartments, villas, or commercial complexes. In return, you receive <strong className="text-foreground-950">35–45% of the developed built-up area</strong> or its equivalent cash value. Your land title remains with you throughout.
              </p>
              <p className="text-foreground-600 leading-relaxed">
                This is not a sale. This is<strong className="text-foreground-950"> development partnership</strong> — the single most lucrative path for landowners who own plots in growing urban corridors across India.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20handshake%20between%20landowner%20and%20real%20estate%20developer%20in%20modern%20glass%20office%2C%20architectural%20blueprints%20and%203D%20building%20model%20on%20table%2C%20warm%20amber%20lighting%2C%20cinematic%20business%20photography%2C%20trust%20and%20partnership%20mood%2C%20India%20corporate%20setting%2C%20high%20detail&width=800&height=600&seq=jda-handshake-01&orientation=landscape"
                alt="JDA Partnership"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Process</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">How a JDA Works — Step by Step</h2>
            <p className="text-foreground-600">From idle land to developed property. Our 5-step process handles everything.</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <div key={step.step} className="flex gap-6 pb-10 relative">
                {i < steps.length - 1 && <div className="absolute left-[26px] top-14 bottom-0 w-px bg-primary-900/20" />}
                <div className="w-[52px] h-[52px] flex items-center justify-center rounded-full bg-primary-500/10 border-2 border-primary-500/30 shrink-0 z-10">
                  <span className="text-sm font-display font-bold text-primary-500">{step.step}</span>
                </div>
                <div className="pt-2">
                  <div className="flex items-center gap-3 mb-2">
                    <i className={`${step.icon} text-xl text-primary-500`} />
                    <h3 className="font-display font-semibold text-lg text-foreground-950">{step.title}</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Benefits</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Why Landowners Choose JDA</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((b) => (
              <div key={b.title} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4">
                  <i className={`${b.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-2">{b.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-16 bg-background-200/50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
            {[
              { value: "200+", label: "JDAs Structured" },
              { value: "35–45%", label: "Owner Share" },
              { value: "12–24 Mo", label: "Construction Timeline" },
              { value: "0", label: "Capital Required From You" },
            ].map((stat) => (
              <div key={stat.label} className="p-5">
                <div className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-1">{stat.value}</div>
                <div className="text-xs text-foreground-500 font-medium uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Ideal Land Types</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Is Your Land Right for a JDA?</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { check: true, label: "Urban or semi-urban plots within city limits" },
              { check: true, label: "Minimum 1,000 sq.m (approx 10,800 sq.ft)" },
              { check: true, label: "Clear title and encumbrance-free" },
              { check: true, label: "Good road access and connectivity" },
              { check: true, label: "Growing neighborhood with rising demand" },
              { check: false, label: "Agricultural land with conversion restrictions" },
              { check: false, label: "Landlocked plots with no road access" },
              { check: false, label: "Properties under active litigation" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3 p-4 rounded-xl card-dark">
                <i className={`${item.check ? "ri-checkbox-circle-fill text-primary-500" : "ri-close-circle-fill text-foreground-400"} text-lg shrink-0`} />
                <span className={`text-sm ${item.check ? "text-foreground-800" : "text-foreground-400"}`}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold text-foreground-950">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`rounded-xl border transition-all duration-300 ${openFaq === i ? "bg-background-200 border-primary-700/40" : "card-dark"}`}>
                <button className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-display font-semibold text-foreground-950 pr-4">{faq.q}</span>
                  <i className={`ri-add-line text-primary-500 text-xl shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-6 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            Ready to Find Your Developer Partner?
          </h2>
          <p className="text-foreground-600 mb-8">
            Get your AI Feasibility Report and see exactly which developers are actively looking for land like yours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/feasibility-report" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              Get My AI Report — ₹4,999
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-primary-700/50 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap">
              Talk to a Strategist
              <i className="ri-message-3-line" />
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}